import { useEffect, useState } from "react";
import { Icon } from "../Icon";
import { splitValues } from "../../utils/listChoices";

/* The Filters sheet both home lists share. A page hands it its groups - for cars: the
   car, the driver, the price; for hire drivers: the driver, the trip - and it hands
   back the chosen values, stored exactly as the chips and the matchers read them
   (comma lists: "5-seater,7-seater").

   The button at the bottom counts what the choice would show before it is applied,
   and will not apply a choice that shows nothing - the dead end is avoided rather than
   explained afterwards.

   groups: [{ title, rows: [{ key, label, options: [{ value, label }], note, disabled }] }] */
export default function FilterSheet({ open, onClose, title, groups, values, onApply, countFor, showLabel, clearLabel, closeLabel }) {
  const [local, setLocal] = useState(values);

  // Start from what is applied each time it opens; a half-made choice that was closed
  // without applying is not kept.
  useEffect(() => {
    if (open) setLocal(values);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // The page behind stays still while the sheet is up.
  useEffect(() => {
    if (!open) return undefined;
    const before = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = before; };
  }, [open]);

  if (!open) return null;

  const toggle = (key, value) => setLocal((prev) => {
    // A typed-in price range from before (CUSTOM:3000-8000) cannot be mixed with the
    // ranges here, so picking one replaces it.
    const now = splitValues(prev[key]).filter((v) => !v.startsWith("CUSTOM:"));
    const next = now.includes(value) ? now.filter((v) => v !== value) : [...now, value];
    return { ...prev, [key]: next.join(",") };
  });
  const clear = () => setLocal((prev) => Object.fromEntries(Object.keys(prev).map((k) => [k, ""])));
  const count = countFor(local);
  // Clearing everything can always be applied, even where the city has no cars.
  const blocked = count === 0 && Object.values(local).some(Boolean);

  return (
    <div className="hm fixed inset-0 z-[110] flex items-end justify-center" role="dialog" aria-modal="true" aria-label={title}>
      <button type="button" aria-label={closeLabel} onClick={onClose} className="absolute inset-0 w-full h-full bg-[rgba(15,23,42,0.45)] cursor-default" />
      <div className="relative w-full max-w-lg max-h-[88vh] bg-white rounded-t-[22px] shadow-[0_-8px_30px_rgba(0,0,0,0.18)] flex flex-col">
        <div className="shrink-0 px-4 pt-2.5 pb-1">
          <div className="w-10 h-1 rounded-full bg-[#E2E8F0] mx-auto mb-2" />
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-[14px] font-extrabold text-[#0F172A]">{title}</h2>
            <button type="button" onClick={onClose} aria-label={closeLabel} title={closeLabel}
              className="w-[40px] h-[40px] rounded-full bg-[#F1F5F9] text-[#475569] flex items-center justify-center active:scale-95">
              <Icon name="x" size={16} strokeWidth={2.4} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto overscroll-contain px-4 pb-4">
          {groups.map((g) => (
            <section key={g.title} className="pt-3 mt-2 first:mt-0 border-t border-[#EEF2F7] first:border-t-0">
              <p className="text-[10px] font-extrabold text-[#5E8700] tracking-wide">{g.title}</p>
              {g.rows.map((r) => (
                <div key={r.key} className="mt-2.5">
                  {r.label && <p className="text-[10.5px] font-bold text-[#1F2937] mb-1.5">{r.label}</p>}
                  {r.note && <p className="text-[9.5px] font-semibold text-[#B45309] mb-1.5 leading-snug">{r.note}</p>}
                  <div className="flex flex-wrap gap-1.5">
                    {r.options.map((o) => {
                      const on = splitValues(local[r.key]).includes(o.value);
                      return (
                        <button key={o.value} type="button" disabled={r.disabled} onClick={() => toggle(r.key, o.value)}
                          aria-pressed={on}
                          className={`min-h-[36px] px-3 rounded-full border text-[10.5px] font-semibold flex items-center gap-1 transition-colors active:scale-[0.97] disabled:opacity-40 ${
                            on ? "bg-[#F1F7E3] border-[#7BAE00] text-[#3F5E00]" : "bg-white border-[#E5E7EB] text-[#374151]"}`}>
                          {on && <Icon name="check" size={12} strokeWidth={3} />}
                          {o.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </section>
          ))}
        </div>

        <div className="shrink-0 border-t border-[#E5E7EB] px-4 pt-2.5 flex gap-2"
          style={{ paddingBottom: "calc(10px + env(safe-area-inset-bottom, 0px))" }}>
          <button type="button" onClick={clear}
            className="flex-1 min-h-[46px] rounded-xl border border-[#CBD5E1] bg-white text-[11.5px] font-bold text-[#334155] active:scale-[0.98]">
            {clearLabel}
          </button>
          <button type="button" onClick={() => onApply(local)} disabled={blocked}
            className="flex-[2] min-h-[46px] rounded-xl bg-[#7BAE00] text-white text-[11.5px] font-bold shadow-md active:scale-[0.98] disabled:bg-[#9CA3AF] disabled:shadow-none">
            {showLabel(count)}
          </button>
        </div>
      </div>
    </div>
  );
}
